import { IAttachment, IChecklist } from '../../models/Checklist'
import { postData, deleteData, getData } from './apiClient'
import { UnauthorisedError } from '../../errors/UnauthorisedError'
import { BadRequestError } from '../../errors/BadRequestError'
import { ForbiddenError } from '../../errors/ForbiddenError'
import { NotFoundError } from '../../errors/NotFoundError'
import { TimeoutError } from '../../errors/TimeoutError'
import { InternalServerError } from '../../errors/InternalServerError'
import {
  IAttachmentResponse,
  IResponse,
  ISubmitAttachmentResponse
} from '../../models/Entity'
import { IIncidentEmail, IIncidentEmailResponse } from '../../models/Incident'
import { TemplateType } from '../../models/Template'


export type ScheduleType = 'completed' | 'week' | 'overdue' | 'today'

export interface IApi {
  getAuthenticatedPhotoUrl(url: string): string
  saveAttachment(attachment: IAttachment): Promise<IAttachmentResponse>
  deleteAttachment(attachment: IAttachment): Promise<string>
  viewAttachment(attachment: IAttachment): void
  getIncidentEmail(
    incidentId: number,
    franchiseId: number,
    franchiseeId: number,
    templateType?: TemplateType
  ): Promise<IIncidentEmailResponse>
  sendIncidentEmail(
    incidentId: number,
    incidentEmail: IIncidentEmail,
    franchiseId: number,
    franchiseeId: number
  ): Promise<void>
}

export interface IApiClientConfig {
  url: string
  header?: any
  timeout?: number //TODO there's already a defaultTimeout in the client interface
}

// this is for the example folder only, each client should implement there own
export class Api implements IApi {
  clientConfig: IApiClientConfig = {} as IApiClientConfig

  constructor(cfg?: IApiClientConfig) {
    if (cfg) {
      this.clientConfig = cfg
    }
  }

  getAuthenticatedPhotoUrl(url: string): string {
    // Adds access token to URL if it is on the API
    const photoUrl = url
    return photoUrl
  }

  async saveAttachment(attachment: IAttachment): Promise<IAttachmentResponse> {
    const attachmentFormData = new FormData()
    attachmentFormData.append('attachment', JSON.stringify(attachment))

    /*const blobData: Blob = await fetch(attachment.localObjectUrl!).then((url) =>
      url.blob()
    )*/
    attachmentFormData.append('file', attachment.file!, attachment.fileName)

    return postData(
      this.clientConfig.url,
      this.clientConfig.header,
      attachmentFormData,
      this.clientConfig.timeout
    )
      .then((data: [number, ISubmitAttachmentResponse]) => {
        const [status, response] = data
        if ([200, 201].includes(status)) {
          return response.data
        }
        this.validateResponseStatus(status, 'saveAttachment()')
        throw new Error('Error Adding Attachment.')
      })
      .then((result: IAttachmentResponse) => {
        return { UUID: result.UUID, url: result.url }
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  }

  async deleteAttachment(attachment: IAttachment): Promise<string> {
    const id = attachment.uuid || attachment.id
    const url = this.clientConfig.url.endsWith('/')
      ? this.clientConfig.url + String(id)
      : this.clientConfig.url + '/' + String(id)

    return deleteData(
      url,
      this.clientConfig.header,
      {},
      this.clientConfig.timeout
    )
      .then((data: [number, IResponse]) => {
        const [status, response] = data
        if (status === 200) {
          return response.data
        } else if (status === 204) {
          return ''
        }
        this.validateResponseStatus(status, 'deleteAttachment()')
        throw new Error('Error Deleting Attachment.')
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  }

  viewAttachment(attachment: IAttachment): void {
    window.open(attachment.url!)
  }
  async getIncidentEmail(
    incidentId: number,
    _franchiseId: number,
    _franchiseeId: number,
    _templateType?: TemplateType
  ): Promise<IIncidentEmailResponse> {
    const baseUrl = this.clientConfig.url.replace(/\/$/, '')
    const url = `${baseUrl}/${incidentId}/email`

    return getData(url, this.clientConfig.header, this.clientConfig.timeout)
      .then((data: [number, IIncidentEmailResponse]) => {
        const [status, response] = data
        if ([200, 201].includes(status)) {
          return response
        }
        this.validateResponseStatus(status, 'getIncidentEmail()')
        throw new Error('Error getting incident email.')
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  }

  async sendIncidentEmail(
    incidentId: number,
    incidentEmail: IIncidentEmail,
    _franchiseId: number,
    _franchiseeId: number
  ): Promise<void> {
    const baseUrl = this.clientConfig.url.replace(/\/$/, '')
    const url = `${baseUrl}/${incidentId}/email`

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const headers: Record<string, string> = {
      ...this.clientConfig.header
    }

    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json'
    }

    try {
      const data = await postData(
        url,
        headers, // use safe merged headers
        JSON.stringify(incidentEmail),
        this.clientConfig.timeout
      )
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const [status, message] = data
      if (![200, 201, 204].includes(status)) {
        this.validateResponseStatus(status, message.message)
        throw new Error('Error sending email.')
      }
    } catch (error) {
      return await Promise.reject(error)
    }
  }

  saveIncidentV2(
    _incident: IChecklist,
    _builderType?: TemplateType,
    _isExistingIncident?: boolean
  ): Promise<{ incidentId: number }> {
    throw new Error('Method not implemented.')
  }

  private validateResponseStatus = (status: number, errorMsg: string) => {
    switch (status) {
      case 401:
        throw new UnauthorisedError(errorMsg)
      case 0:
        throw new UnauthorisedError(errorMsg)
      case 400:
        throw new BadRequestError(errorMsg)
      case 403:
        throw new ForbiddenError(errorMsg)
      case 404:
        throw new NotFoundError(errorMsg)
      case 408:
        throw new TimeoutError(errorMsg)
      case 500:
        throw new InternalServerError(errorMsg)
      case 501:
        throw new InternalServerError(errorMsg)
      case 502:
        throw new InternalServerError(errorMsg)
      case 503:
        throw new InternalServerError(errorMsg)
      case 504:
        throw new InternalServerError(errorMsg)
    }
  }
}

export default Api
